import React, { useState, useEffect, useRef } from 'react';
import { FaPlay, FaPause, FaTimes, FaVolumeUp, FaVolumeMute, FaBackward, FaForward, FaMusic, FaMinus, FaExpand } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

interface Song {
  id: string;
  song_url: string;
  song_cover: string;
  creator: string;
  language: string;
  song_lyrics: string;
  is_free: boolean;
}

interface SongListeningSectionProps {
  song: Song;
  onClose: () => void;
}

interface LoopMarker {
  startTime: number;
  endTime: number;
  isActive: boolean;
}

export default function SongListeningSection({ song, onClose }: SongListeningSectionProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [loopMarker, setLoopMarker] = useState<LoopMarker | null>(null);
  const [isLooping, setIsLooping] = useState(false);
  const [showLyrics, setShowLyrics] = useState(true);
  const [isMarkingStart, setIsMarkingStart] = useState(false);
  const [tempStartTime, setTempStartTime] = useState<number | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [currentParagraphIndex, setCurrentParagraphIndex] = useState(0);
  const [lyricParagraphs, setLyricParagraphs] = useState<string[]>([]);
  
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Split lyrics into paragraphs on component mount
  useEffect(() => {
    const paragraphs = song.song_lyrics
      .split(/\n\s*\n/)
      .map(paragraph => paragraph.trim())
      .filter(paragraph => paragraph.length > 0 && !paragraph.match(/^\[.*\]$/));
    
    setLyricParagraphs(paragraphs);
  }, [song.song_lyrics]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [song]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (audioRef.current.paused) {
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            setIsPlaying(true);
          }).catch(error => {
            console.error('Error playing audio:', error);
            setIsPlaying(false);
            toast.error('Failed to play audio. Please try again.');
          });
        }
      } else {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const current = audioRef.current.currentTime;
      setCurrentTime(current);

      // Handle looping for marked segment
      if (loopMarker && isLooping && current >= loopMarker.endTime) {
        audioRef.current.currentTime = loopMarker.startTime;
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleCanPlay = () => {
    console.log('Audio can play');
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleError = (e: React.SyntheticEvent<HTMLAudioElement, Event>) => {
    console.error('Audio error occurred:', e);
    setIsPlaying(false);
    toast.error('Audio failed to load. Please check your connection.');
  };

  const handleSeek = (time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!progressBarRef.current || !duration || duration === 0) {
      console.log('Progress bar click ignored - no duration or ref');
      return;
    }
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const clickTime = percentage * duration;
    
    console.log('Progress bar clicked:', { x, percentage, clickTime, duration });
    
    if (isMarkingStart) {
      // First click - set start time
      setTempStartTime(clickTime);
      setIsMarkingStart(false);
      toast('Now click where you want the loop to end');
      console.log('Start time set:', clickTime);
    } else if (tempStartTime !== null) {
      // Second click - set end time and create loop
      if (clickTime > tempStartTime) {
        const newLoopMarker: LoopMarker = {
          startTime: tempStartTime,
          endTime: clickTime,
          isActive: true
        };
        setLoopMarker(newLoopMarker);
        setIsLooping(true);
        
        // Seek to start of loop
        handleSeek(tempStartTime);
        
        toast.success(`Loop set: ${formatTime(tempStartTime)} - ${formatTime(clickTime)}`);
        console.log('Loop created:', newLoopMarker);
      } else {
        toast.error('End time must be after start time');
      }
      setTempStartTime(null);
    } else {
      // Normal seek
      handleSeek(clickTime);
      console.log('Seeking to:', clickTime);
    }
  };

  const startMarkingLoop = () => {
    if (!duration || duration === 0) {
      toast.error('Please wait for the song to load before marking loops');
      return;
    }
    
    setIsMarkingStart(true);
    setTempStartTime(null);
    setLoopMarker(null);
    setIsLooping(false);
    toast('Click on the progress bar where you want the loop to start');
    console.log('Started marking loop mode');
  };

  const clearLoop = () => {
    setLoopMarker(null);
    setIsLooping(false);
    setTempStartTime(null);
    setIsMarkingStart(false);
    toast.success('Loop cleared');
  };

  const handleSkip = (seconds: number) => {
    if (audioRef.current) {
      const newTime = Math.max(0, Math.min(currentTime + seconds, duration));
      audioRef.current.currentTime = newTime;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePreviousParagraph = () => {
    setCurrentParagraphIndex(prev => Math.max(0, prev - 1));
  };

  const handleNextParagraph = () => {
    setCurrentParagraphIndex(prev => Math.min(lyricParagraphs.length - 1, prev + 1));
  };

  return (
    <motion.div
      className={`fixed right-0 top-0 bg-gradient-to-b from-gray-900 via-black to-gray-800 border-l border-gray-700 shadow-2xl z-40 overflow-hidden transition-all duration-300 ${
        isMinimized && isMobile 
          ? 'h-20 w-full' 
          : isMobile 
            ? 'h-full w-full' 
            : 'h-full w-96'
      }`}
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
    >
      {/* Minimized View for Mobile */}
      {isMinimized && isMobile ? (
        <div className="p-4 bg-black/50 backdrop-blur-lg h-full">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src={song.song_cover}
                alt={song.creator}
                className="w-12 h-12 rounded-lg object-cover border border-gray-600"
              />
              <div className="flex-1 min-w-0">
                <h4 className="text-white font-semibold truncate text-sm">{song.creator}</h4>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-400 text-xs capitalize">{song.language}</span>
                  {song.is_free && (
                    <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                      Free
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={handlePlayPause}
                className="p-2 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 transition-all duration-200"
              >
                {isPlaying ? (
                  <FaPause className="w-4 h-4 text-black" />
                ) : (
                  <FaPlay className="w-4 h-4 text-black ml-0.5" />
                )}
              </button>

              <button
                onClick={() => setIsMinimized(false)}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                <FaExpand className="w-4 h-4 text-gray-400 hover:text-white" />
              </button>

              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                <FaTimes className="w-4 h-4 text-gray-400 hover:text-white" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="p-4 border-b border-gray-700 bg-black/50 backdrop-blur-lg">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <FaMusic className="w-5 h-5 text-yellow-400" />
                <h3 className="text-lg font-bold text-white">Song Player</h3>
              </div>
              <div className="flex items-center space-x-2">
                {/* Minimize button - only show on mobile */}
                {isMobile && (
                  <button
                    onClick={() => setIsMinimized(true)}
                    className="p-2 rounded-full hover:bg-white/10 transition-colors"
                    title="Minimize player"
                  >
                    <FaMinus className="w-4 h-4 text-gray-400 hover:text-white" />
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-white/10 transition-colors"
                >
                  <FaTimes className="w-4 h-4 text-gray-400 hover:text-white" />
                </button>
              </div>
            </div>

            {/* Song Info */}
            <div className="flex items-center space-x-3">
              <img
                src={song.song_cover}
                alt={song.creator}
                className="w-12 h-12 rounded-lg object-cover border border-gray-600"
              />
              <div className="flex-1 min-w-0">
                <h4 className="text-white font-semibold truncate">{song.creator}</h4>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-400 text-sm capitalize">{song.language}</span>
                  {song.is_free && (
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                      Free
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Audio Controls */}
          <div className="p-4 border-b border-gray-700 bg-gray-900/50">
            {/* Progress Bar */}
            <div 
              ref={progressBarRef}
              className="relative w-full h-3 bg-gray-700 rounded-full mb-4 cursor-pointer hover:h-4 transition-all duration-200 select-none"
              onClick={handleProgressBarClick}
              style={{ userSelect: 'none' }}
            >
              {/* Main progress */}
              <div 
                className="absolute left-0 top-0 h-full bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full transition-all duration-100"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              />
              
              {/* Loop marker indicator */}
              {loopMarker && (
                <div
                  className="absolute top-0 h-full bg-red-400/40 rounded-full border-2 border-red-400"
                  style={{
                    left: `${(loopMarker.startTime / duration) * 100}%`,
                    width: `${((loopMarker.endTime - loopMarker.startTime) / duration) * 100}%`
                  }}
                />
              )}
              
              {/* Temp start marker */}
              {tempStartTime !== null && (
                <div
                  className="absolute top-0 h-full w-1 bg-blue-400"
                  style={{ left: `${(tempStartTime / duration) * 100}%` }}
                />
              )}
              
              {/* Click instruction overlay */}
              {isMarkingStart && (
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none">
                  Click to set start time
                </div>
              )}
              {tempStartTime !== null && !isMarkingStart && (
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none">
                  Click to set end time
                </div>
              )}
            </div>

            {/* Time Display */}
            <div className="flex justify-between text-xs text-gray-400 mb-4">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>

            {/* Control Buttons */}
            <div className="flex items-center justify-center space-x-4 mb-4">
              <button
                onClick={() => handleSkip(-10)}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <FaBackward className="w-4 h-4 text-white" />
              </button>

              <button
                onClick={handlePlayPause}
                className="p-3 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 transition-all duration-200 transform hover:scale-105"
              >
                {isPlaying ? (
                  <FaPause className="w-5 h-5 text-black" />
                ) : (
                  <FaPlay className="w-5 h-5 text-black ml-0.5" />
                )}
              </button>

              <button
                onClick={() => handleSkip(10)}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <FaForward className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* Loop Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setIsMuted(!isMuted)}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                >
                  {isMuted ? (
                    <FaVolumeMute className="w-4 h-4 text-gray-400" />
                  ) : (
                    <FaVolumeUp className="w-4 h-4 text-white" />
                  )}
                </button>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.1}
                  value={volume}
                  onChange={(e) => setVolume(parseFloat(e.target.value))}
                  className="w-16 h-1 bg-gray-700 rounded-full appearance-none cursor-pointer"
                />
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={startMarkingLoop}
                  disabled={isMarkingStart}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    isMarkingStart
                      ? 'bg-blue-400/20 text-blue-400 cursor-not-allowed'
                      : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                  }`}
                >
                  {isMarkingStart ? 'Marking...' : 'Mark Loop'}
                </button>
                
                {loopMarker && (
                  <button
                    onClick={clearLoop}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                  >
                    Clear Loop
                  </button>
                )}
              </div>
            </div>

            {/* Loop Status */}
            {loopMarker && isLooping && (
              <div className="mt-3 p-2 bg-red-500/20 rounded-lg border border-red-500/30">
                <div className="flex items-center justify-between">
                  <span className="text-red-300 text-xs">
                    🔄 Looping: {formatTime(loopMarker.startTime)} - {formatTime(loopMarker.endTime)}
                  </span>
                  <button
                    onClick={clearLoop}
                    className="text-red-400 hover:text-red-300 text-xs"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Lyrics Section */}
          <div className="flex-1 overflow-hidden">
            <div className="p-4 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h4 className="text-white font-semibold">Lyrics</h4>
                <button
                  onClick={() => setShowLyrics(!showLyrics)}
                  className="text-gray-400 hover:text-white text-sm"
                >
                  {showLyrics ? 'Hide' : 'Show'}
                </button>
              </div>
              <p className="text-gray-400 text-xs mt-1">
                Mark sections on the progress bar to loop specific parts
              </p>
            </div>

            <AnimatePresence>
              {showLyrics && (
                <motion.div
                  className="p-4 scrollbar-hide"
                  style={{ height: 'calc(100vh - 400px)', maxHeight: '500px' }}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Navigation Header */}
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={handlePreviousParagraph}
                      disabled={currentParagraphIndex === 0}
                      className="p-2 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    
                    <div className="text-center">
                      <span className="text-white/60 text-xs">
                        Paragraph {currentParagraphIndex + 1} of {lyricParagraphs.length}
                      </span>
                    </div>
                    
                    <button
                      onClick={handleNextParagraph}
                      disabled={currentParagraphIndex === lyricParagraphs.length - 1}
                      className="p-2 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  {/* Current Paragraph Display */}
                  {lyricParagraphs.length > 0 && (
                    <motion.div
                      key={currentParagraphIndex}
                      className="p-4 rounded-lg bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 transition-all duration-300 min-h-[200px] flex items-center justify-center"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <p className="text-sm leading-relaxed whitespace-pre-line text-center">
                        {lyricParagraphs[currentParagraphIndex]}
                      </p>
                    </motion.div>
                  )}

                  {/* Navigation Dots */}
                  <div className="flex justify-center space-x-2 mt-4">
                    {lyricParagraphs.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentParagraphIndex(index)}
                        className={`w-2 h-2 rounded-full transition-all duration-200 ${
                          index === currentParagraphIndex
                            ? 'bg-yellow-400 w-6'
                            : 'bg-white/30 hover:bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Instructions */}
          <div className="p-4 border-t border-gray-700 bg-gray-900/50">
            <div className="bg-white/5 rounded-lg p-3 border border-white/10">
              <h5 className="text-white font-medium text-sm mb-2 flex items-center">
                <FaMusic className="w-4 h-4 mr-2 text-yellow-400" />
                How to Use:
              </h5>
              <ul className="text-xs text-gray-300 space-y-1">
                <li>• Click "Mark Loop" then click twice on the progress bar</li>
                <li>• First click sets start time, second click sets end time</li>
                <li>• The marked section will loop automatically</li>
                <li>• Perfect for practicing specific parts of the song</li>
                {isMobile && (
                  <li>• Use minimize button to chat while listening</li>
                )}
              </ul>
            </div>
          </div>
        </>
      )}

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        src={song.song_url}
        preload="metadata"
        crossOrigin="anonymous"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onCanPlay={handleCanPlay}
        onPlay={handlePlay}
        onPause={handlePause}
        onError={handleError}
        onEnded={() => {
          if (!isLooping || !loopMarker) {
            setIsPlaying(false);
          } else {
            // Loop back to start of marked segment
            if (audioRef.current && loopMarker) {
              audioRef.current.currentTime = loopMarker.startTime;
            }
          }
        }}
      />

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
        {/* Floating Musical Notes */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-yellow-400/10"
            initial={{ 
              x: Math.random() * 100 + '%', 
              y: Math.random() * 100 + '%',
              rotate: Math.random() * 360,
              scale: Math.random() * 0.5 + 0.5
            }}
            animate={{ 
              y: [null, '-20%'],
              rotate: [null, Math.random() * 360 + 180],
              opacity: [0.1, 0.3, 0.1]
            }}
            transition={{ 
              duration: Math.random() * 15 + 20, 
              repeat: Infinity,
              ease: "linear"
            }}
            style={{ 
              fontSize: `${Math.random() * 2 + 1}rem`,
            }}
          >
            {['♪', '♫', '♬', '♩'][Math.floor(Math.random() * 4)]}
          </motion.div>
        ))}

        {/* Gradient Orbs */}
        <motion.div 
          className="absolute top-20 right-10 w-32 h-32 bg-yellow-400/10 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute bottom-20 left-10 w-24 h-24 bg-red-400/10 rounded-full blur-3xl"
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.4, 0.7, 0.4]
          }}
          transition={{ 
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        /* Hide scrollbar but keep functionality */
        .scrollbar-hide {
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;  /* Firefox */
        }
        
        .scrollbar-hide::-webkit-scrollbar {
          display: none;  /* Chrome, Safari, Opera */
        }
        
        /* Fallback for other overflow-y-auto elements */
        .overflow-y-auto:not(.scrollbar-hide) {
          scrollbar-width: thin;
          scrollbar-color: rgba(75, 85, 99, 0.8) transparent;
        }
        
        .overflow-y-auto:not(.scrollbar-hide)::-webkit-scrollbar {
          width: 4px;
        }
        
        .overflow-y-auto:not(.scrollbar-hide)::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .overflow-y-auto:not(.scrollbar-hide)::-webkit-scrollbar-thumb {
          background-color: rgba(75, 85, 99, 0.8);
          border-radius: 2px;
        }
        
        .overflow-y-auto:not(.scrollbar-hide)::-webkit-scrollbar-thumb:hover {
          background-color: rgba(107, 114, 128, 1);
        }

        /* Volume slider styling */
        input[type="range"] {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          cursor: pointer;
        }

        input[type="range"]::-webkit-slider-track {
          background: #374151;
          height: 4px;
          border-radius: 2px;
        }

        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          background: #fbbf24;
          height: 12px;
          width: 12px;
          border-radius: 50%;
          cursor: pointer;
        }

        input[type="range"]::-moz-range-track {
          background: #374151;
          height: 4px;
          border-radius: 2px;
          border: none;
        }

        input[type="range"]::-moz-range-thumb {
          background: #fbbf24;
          height: 12px;
          width: 12px;
          border-radius: 50%;
          cursor: pointer;
          border: none;
        }
      `}</style>
    </motion.div>
  );
}